import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, MapPin, Star, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/hero-buggy-adventure.jpg";

export interface TourCardTour {
  id: string;
  title: string;
  slug: string;
  short_description?: string | null;
  description?: string | null;
  main_image_url?: string | null;
  location?: string | null;
  duration?: string | null;
  base_price: number;
  person_capacity?: number | null;
  is_featured?: boolean | null;
}

interface TourCardProps {
  tour: TourCardTour;
  price?: number;
  priority?: boolean;
}

export default function TourCard({ tour, price = tour.base_price, priority = false }: TourCardProps) {
  return (
    <article className="group flex h-full min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-card transition duration-300 hover:-translate-y-1 hover:shadow-card-hover">
      <Link to={`/tour/${tour.slug}`} className="relative block aspect-[4/3] overflow-hidden" aria-label={`View ${tour.title}`}>
        <img
          src={tour.main_image_url || heroImage}
          alt={tour.title}
          width={640}
          height={480}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 motion-safe:group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-card-image-overlay" />
        {tour.is_featured && (
          <Badge className="absolute left-3 top-3 gap-1 border-0 bg-primary text-primary-foreground shadow-sm">
            <Star className="h-3.5 w-3.5 fill-current" aria-hidden="true" /> Featured
          </Badge>
        )}
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2 text-hero-foreground">
          <div className="min-w-0">
            <span className="text-[11px] font-semibold uppercase text-hero-muted">Starting from</span>
            <p className="font-display text-3xl leading-none">${Number(price || 0).toFixed(0)}</p>
          </div>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-hero-border bg-hero-surface backdrop-blur-md transition-colors group-hover:bg-primary">
            <ArrowUpRight className="h-5 w-5" aria-hidden="true" />
          </span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase text-primary">
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="truncate">{tour.location || "Punta Cana"}</span>
        </div>
        <h3 className="font-display text-2xl leading-[1.05] text-card-foreground">
          <Link to={`/tour/${tour.slug}`} className="transition-colors hover:text-primary">{tour.title}</Link>
        </h3>
        <p className="mt-3 line-clamp-2 text-sm leading-6 text-muted-foreground">
          {tour.short_description || tour.description || "A guided off-road experience through Punta Cana's most memorable landscapes."}
        </p>
        <div className="mt-5 grid grid-cols-2 gap-2 border-y border-border py-3 text-xs text-muted-foreground">
          <span className="flex min-w-0 items-center gap-1.5"><Clock className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" /><span className="truncate">{tour.duration || "Half day"}</span></span>
          <span className="flex min-w-0 items-center justify-end gap-1.5"><Users className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" /><span className="truncate">Up to {tour.person_capacity || 4}</span></span>
        </div>
        <Button asChild className="mt-5 w-full font-semibold" aria-label={`Book ${tour.title}`}>
          <Link to={`/tour/${tour.slug}`}>Explore this tour <ArrowUpRight className="h-4 w-4" aria-hidden="true" /></Link>
        </Button>
      </div>
    </article>
  );
}
